/**
 * Slice 44 — the Meta ad account on Analytics: the whole account and its ad
 * surfaces over the same two days the period picker holds, read by Meta in
 * the account's own zone.
 *
 * ## THE AD ACCOUNT, AND SAID SO
 *
 * The card is titled "Meta ad account", not "Meta ads": what it reads is the
 * whole account — campaigns an agency runs straight in Ads Manager included —
 * while everything else on this page is this workspace's own campaigns, and
 * Reach's "Meta ads" page (slice 65; the campaigns page's panel before it) is
 * only the ones composed in Wend. One
 * name for two populations is the trap every figure here is kept apart from.
 * The account's name, when Meta gave one, leads the tag.
 *
 * ## ITS OWN READ, ITS OWN DAYS, ITS OWN TAG
 *
 * Everything else on the page is one read of this workspace's campaigns and
 * delivery events. This card is a second read, of Meta, and its tag prints
 * the days Wend asked Meta for — the picker's two dates, in the page's own
 * date style. The account's zone is printed only when it is not the zone the
 * page reads its period in: then the dates match the ones above and the
 * instants do not, and the tag says whose zone they are. Two names for one
 * zone (`Asia/Kolkata`, `Asia/Calcutta`) are the same zone, and print nothing.
 * No figure here is added to any figure above: an impression is not a
 * delivery, and Meta's "people reached" is not this workspace's contacts.
 *
 * ## A READ THAT FAILED IS NEVER THE ONE BEFORE IT
 *
 * When the last read failed — Meta's limit, the network — the card draws "Not
 * read" and a retry, never the previous period's figures: those would sit
 * under this period's report with nothing but a small tag to say they are
 * another month's. While a new read is in flight the card keeps the last
 * figures under their own days, as the report above keeps its own under its
 * own period; it is a failure, not a wait, that is never drawn as the figures
 * before it. And one outage is one alert: the page's own error is the alert,
 * so this is a chip, not a second `ErrorState` — and when the report itself
 * did not load, the page does not draw this card at all.
 *
 * ## NO CARD FOR A SURFACE THIS WORKSPACE DOES NOT USE
 *
 * No Meta ad account connected is no card at all — the page does not even
 * ask. Connected but untested, or without the ads permission, is the
 * Integrations tab's chip for the same state, and a read Meta refused is its
 * own repair sentence with a retry.
 *
 * Pure: the page fetches and hands the state in, so this renders in the
 * screen suite without a browser.
 */
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, StateChip } from '@wend/ui';

/* `@wend/shared` directly, never through the API client. */
import { parseCalendarDate, type MetaAdsPeriodState } from '@wend/shared';

import { MetaAdSurfacePanels } from '../meta-ads/MetaAdSurfaces';

export interface AnalyticsAdsProps {
  /** The read's answer, or `null` before the first one arrives. */
  readonly state: MetaAdsPeriodState | null;
  /** The last read failed: "Not read" and a retry, never the figures before it. */
  readonly unread: boolean;
  readonly onRetry: () => void;
  /** The ad account's name, when Meta gave one. */
  readonly accountName: string | null;
  /** The zone the page reads its period in. */
  readonly pageZone: string;
}

/** `2026-09-01` → the day in the page's style, as the date it names, whatever zone this browser is in. */
export function calendarDay(value: string): string {
  const parts = parseCalendarDate(value);
  if (parts === null) return value;
  /* `formatInstantInZone`'s options, less the time: one date style on one page. */
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
  );
}

/** The zone as the platform names it — so two names for one zone compare equal. */
function canonicalZone(zone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: zone }).resolvedOptions().timeZone;
  } catch {
    return zone;
  }
}

export function AnalyticsAds({ state, unread, onRetry, accountName, pageZone }: AnalyticsAdsProps) {
  if (!unread && (state === null || state.state === 'not_connected')) return null;

  return (
    <Card data-role="analytics-ads" data-state={unread ? 'unread' : state?.state}>
      <CardHeader>
        <CardTitle>Meta ad account</CardTitle>
      </CardHeader>
      <CardContent>
        {unread || state === null ? (
          <div className="flex flex-wrap items-center gap-2">
            <StateChip state="not_measured" label="Not read" />
            <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : state.state === 'ok' ? (
          <MetaAdSurfacePanels
            figures={state.read}
            covers={[
              ...(accountName === null ? [] : [accountName]),
              `${calendarDay(state.read.period.since)} – ${calendarDay(state.read.period.until)}`,
              ...(canonicalZone(state.read.period.timezone) === canonicalZone(pageZone) ? [] : [state.read.period.timezone]),
            ].join(' · ')}
          />
        ) : state.state === 'not_checked' ? (
          <StateChip state="not_measured" label="Not checked" />
        ) : state.state === 'not_permitted' ? (
          <Badge tone="bad">No ads permission</Badge>
        ) : state.state === 'failed' ? (
          <div className="flex flex-col items-start gap-3">
            <p className="max-w-prose text-[0.88rem] leading-relaxed text-bad">{state.failure.message}</p>
            <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
